/**
 * Hydrate nodes that carry `cart="..."` attributes and repeat templates for
 * cart-item children.
 *
 * Also wires `[data-action="plus|minus|remove"]` click handlers that mutate
 * the cart via the `cart/state` module.
 *
 * Re-runs automatically on the global `smootify:cart_updated` event.
 */

import { formatMoney } from "../ui/format.js";
import {
  removeLine,
  loadCart,
  updateLine,
  adjustLineQuantity,
  whenCartIdle,
  getCurrentCart,
  applyCouponCode,
  updateCartNote,
  InvalidCouponError,
} from "../cart/state.js";
import { config } from "../config.js";
import { manageCommerceForm, showCommerceError } from "../ui/commerce-form.js";
import type { Cart, CartLine, ShopifyImage } from "../types.js";

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

function setText(el: Element, text: string): void {
  el.textContent = text;
}

function applyImage(el: Element, image: ShopifyImage | null): void {
  if (!image) return;
  if (el instanceof HTMLImageElement) {
    el.src = image.url;
    if (image.altText) el.alt = image.altText;
  } else if (el instanceof HTMLElement) {
    el.style.backgroundImage = `url(${JSON.stringify(image.url)})`;
  }
}

function setHidden(el: Element, hidden: boolean): void {
  el.classList.toggle("lmdr-hidden", hidden);
}

const quantityCommits = new WeakMap<HTMLInputElement, {
  quantity: number;
  promise: Promise<boolean>;
}>();

/** Queue every distinct entered quantity; change + blur share one request. */
async function commitQuantity(
  input: HTMLInputElement,
  lineId: string,
): Promise<boolean> {
  const raw = input.value.trim();
  const parsed = Number(raw);
  const currentQty = getCurrentCart()?.lines.find((line) => line.id === lineId)?.quantity ?? 1;
  const pending = quantityCommits.get(input);
  if (raw === "" || !Number.isSafeInteger(parsed) || parsed < 0) {
    if (raw === "") {
      input.value = String(pending?.quantity ?? currentQty);
      input.removeAttribute("data-lmdr-qty-dirty");
      return true;
    }
    // Keep the invalid draft visible: resetting it on blur would let the
    // following checkout click silently proceed with the previous quantity.
    input.setAttribute("data-lmdr-qty-dirty", "true");
    showCommerceError(input, "Veuillez saisir une quantité entière supérieure ou égale à zéro.");
    return false;
  }
  if (pending?.quantity === parsed) {
    input.removeAttribute("data-lmdr-qty-dirty");
    return pending.promise;
  }
  if (!pending && parsed === currentQty && !input.hasAttribute("data-lmdr-qty-dirty")) return true;

  input.removeAttribute("data-lmdr-qty-dirty");
  input.setAttribute("data-lmdr-committing", "true");
  showCommerceError(input, null);
  const commit = { quantity: parsed, promise: Promise.resolve(true) };
  commit.promise = (async (): Promise<boolean> => {
    try {
      if (parsed === 0) await removeLine(lineId);
      else await updateLine(lineId, parsed);
      return true;
    } catch {
      showCommerceError(input, "Impossible de modifier la quantité. Vérifiez votre connexion et réessayez.");
      return false;
    } finally {
      if (quantityCommits.get(input) === commit) {
        quantityCommits.delete(input);
        input.removeAttribute("data-lmdr-committing");
        // Preserve a newer draft entered while this request was in flight.
        if (input.value.trim() === raw) {
          input.value = String(getCurrentCart()?.lines.find((line) => line.id === lineId)?.quantity ?? currentQty);
        }
      }
    }
  })();
  quantityCommits.set(input, commit);
  return commit.promise;
}

function formatOptions(line: CartLine): string {
  const opts = line.merchandise.selectedOptions.filter(
    (o) => !(o.name === "Title" && o.value === "Default Title"),
  );
  if (opts.length === 0) return "";
  return opts.map((o) => `${o.name}: ${o.value}`).join(" / ");
}

// ---------------------------------------------------------------------------
//  cart="..." nodes
// ---------------------------------------------------------------------------

function hydrateCartIndicators(root: ParentNode, cart: Cart | null): void {
  const nodes = root.querySelectorAll<HTMLElement>("[cart]");
  const empty = !cart || cart.lines.length === 0;

  nodes.forEach((el) => {
    const kind = el.getAttribute("cart") ?? "";
    switch (kind) {
      case "count":
        setText(el, String(cart?.totalQuantity ?? 0));
        break;
      case "total":
        setText(el, cart ? formatMoney(cart.cost.totalAmount) : "");
        break;
      case "subtotal":
        setText(el, cart ? formatMoney(cart.cost.subtotalAmount) : "");
        break;
      case "is-empty":
        setHidden(el, !empty);
        break;
      case "is-not-empty":
        setHidden(el, empty);
        break;
      case "discount-codes": {
        const codes = (cart?.discountCodes ?? [])
          .filter((c) => c.applicable)
          .map((c) => c.code);
        setText(el, codes.join(", "));
        setHidden(el, codes.length === 0);
        break;
      }
      default:
        break;
    }
  });
}

// ---------------------------------------------------------------------------
//  cart-item templates
// ---------------------------------------------------------------------------

function attachLineActions(clone: HTMLElement, line: CartLine): void {
  clone.dataset["lineId"] = line.id;
  if (clone.hasAttribute("data-lmdr-actions-bound")) return;
  clone.setAttribute("data-lmdr-actions-bound", "true");
  clone.querySelectorAll<HTMLButtonElement>("button[data-action]").forEach((button) => {
    button.type = "button";
    if (!button.getAttribute("aria-label")) button.setAttribute("aria-label",
      button.dataset["action"] === "plus" ? "Augmenter la quantité" :
      button.dataset["action"] === "minus" ? "Diminuer la quantité" : "Retirer le produit");
  });
  let pending = 0;

  const onClick = async (ev: Event): Promise<void> => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const button = target.closest<HTMLElement>("[data-action]");
    if (!button || !clone.contains(button)) return;

    const action = button.getAttribute("data-action");
    if (!action) return;

    ev.preventDefault();
    showCommerceError(clone, null);
    pending++;
    clone.setAttribute("data-state", "loading");

    try {
      if (action === "plus") {
        await adjustLineQuantity(line.id, 1);
      } else if (action === "minus") {
        await adjustLineQuantity(line.id, -1);
      } else if (action === "remove") {
        await removeLine(line.id);
      }
    } catch {
      showCommerceError(clone, "Impossible de modifier le panier. Vérifiez votre connexion et réessayez.");
    } finally {
      if (--pending === 0) clone.setAttribute("data-state", "default");
    }
  };

  clone.addEventListener("click", (ev) => {
    void onClick(ev);
  });
}

/**
 * Rend les options d'une ligne panier : cart-item="options" contient un
 * template enfant `<div option="name">...</div><div>:</div><div option="value">...</div>`
 * qui doit être cloné 1x par option sélectionnée.
 */
function fillOptions(optionsEl: HTMLElement, line: CartLine): void {
  // Strip tous les précédents rendus (si re-hydrate).
  optionsEl.querySelectorAll('[data-lmdr-option-rendered="true"]').forEach((n) => n.remove());

  // Filtre l'option par défaut Shopify "Title: Default Title" — elle apparaît
  // sur les produits sans variants multi-options et n'est jamais pertinente
  // à afficher au client.
  const opts = line.merchandise.selectedOptions.filter(
    (o) => !(o.name === "Title" && o.value === "Default Title"),
  );
  if (opts.length === 0) {
    // Aucune option significative → on cache le conteneur entier.
    optionsEl.style.display = "none";
    return;
  }
  optionsEl.style.removeProperty("display");

  // Identifie le template : un descendant avec option="name" ou option="value".
  const nameNode = optionsEl.querySelector<HTMLElement>('[option="name"]');
  const valueNode = optionsEl.querySelector<HTMLElement>('[option="value"]');
  if (!nameNode || !valueNode) {
    // Pas de template child — fallback simple : "Name: value / Name: value"
    setText(optionsEl, formatOptions(line));
    return;
  }

  // Si le template est déjà dans un wrapper direct du conteneur options,
  // on peut cloner le wrapper. Sinon on clone directement nameNode+sep+valueNode.
  // Pragmatique : on clone l'ARBORESCENCE intermédiaire (parent commun).
  const commonParent = findCommonParentInside(optionsEl, nameNode, valueNode);
  if (commonParent && commonParent !== optionsEl) {
    // Template = commonParent. Render : 1 clone par option.
    const template = commonParent;
    template.setAttribute("data-lmdr-option-template", "true");
    template.style.display = "none";
    opts.forEach((opt) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-lmdr-option-template");
      clone.style.removeProperty("display");
      clone.setAttribute("data-lmdr-option-rendered", "true");
      const n = clone.querySelector<HTMLElement>('[option="name"]');
      const v = clone.querySelector<HTMLElement>('[option="value"]');
      if (n) setText(n, opt.name);
      if (v) setText(v, opt.value);
      template.parentElement?.insertBefore(clone, template);
    });
  } else {
    // Le template EST le conteneur lui-même → on remplit direct avec la 1re option.
    setText(nameNode, opts[0]?.name ?? "");
    setText(valueNode, opts[0]?.value ?? "");
  }
}

function findCommonParentInside(
  scope: HTMLElement,
  a: HTMLElement,
  b: HTMLElement,
): HTMLElement | null {
  const ancestors = new Set<Element>();
  let cur: Element | null = a;
  while (cur && cur !== scope) {
    ancestors.add(cur);
    cur = cur.parentElement;
  }
  cur = b;
  while (cur && cur !== scope) {
    if (ancestors.has(cur)) return cur as HTMLElement;
    cur = cur.parentElement;
  }
  return null;
}

function fillCartItem(clone: HTMLElement, line: CartLine): void {
  // Update AUSSI l'input number dans <quantity-input> (pas marqué cart-item=
  // mais le user le voit). Sans ça, cliquer +/- modifie le cart mais le
  // champ visuel reste à "1".
  //
  // On bind aussi :
  //   - keydown Enter → bloqué. Sans ça le form cart-item submit → fixupCartForms
  //     intercepte → goToCheckout() redirige l'user vers Shopify checkout
  //     (UX cassée : l'user voulait juste valider la qty).
  //   - change/blur → updateLine(line.id, nouvelle_qty) pour que la saisie
  //     directe au clavier mette à jour le panier (avant : aucun handler,
  //     la qty tapée était visuelle uniquement).
  clone
    .querySelectorAll<HTMLInputElement>('quantity-input input[type="number"], input[name="quantity"]')
    .forEach((inp) => {
      // Preserve a quantity currently being edited while another line updates.
      if (document.activeElement !== inp && !inp.hasAttribute("data-lmdr-committing") && !inp.hasAttribute("data-lmdr-qty-dirty")) {
        inp.value = String(line.quantity);
      }
      if (inp.hasAttribute("data-lmdr-qty-bound")) return;
      inp.setAttribute("data-lmdr-qty-bound", "true");
      inp.addEventListener("input", () => { inp.setAttribute("data-lmdr-qty-dirty", "true"); });
      inp.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          ev.stopPropagation();
          // Commit immédiat de la valeur tapée, puis blur pour fermer le focus.
          void commitQuantity(inp, line.id);
          inp.blur();
        }
      });
      inp.addEventListener("change", () => {
        void commitQuantity(inp, line.id);
      });
      // blur : commit aussi au cas où l'user sort du champ sans Enter ni change
      // (browsers firent "change" au blur si la valeur a changé, mais on met
      // ceinture + bretelles).
      inp.addEventListener("blur", () => {
        void commitQuantity(inp, line.id);
      });
    });

  clone
    .querySelectorAll<HTMLElement>("[cart-item]")
    .forEach((child) => {
      const kind = child.getAttribute("cart-item") ?? "";
      switch (kind) {
        case "image": {
          const target =
            child instanceof HTMLImageElement
              ? child
              : child.querySelector("img") ?? child;
          // Variant-specific image prioritaire, sinon featured image produit.
          const img = line.merchandise.image ?? line.merchandise.product.featuredImage ?? null;
          applyImage(target, img);
          break;
        }
        case "title":
          setText(child, line.merchandise.product.title);
          break;
        case "price":
          setText(child, formatMoney(line.cost.amountPerQuantity));
          break;
        case "quantity":
          if (child instanceof HTMLInputElement) {
            if (document.activeElement !== child && !child.hasAttribute("data-lmdr-committing") && !child.hasAttribute("data-lmdr-qty-dirty")) {
              child.value = String(line.quantity);
            }
          } else {
            setText(child, String(line.quantity));
          }
          break;
        case "options":
          fillOptions(child, line);
          break;
        case "total":
        case "line-price":
          setText(child, formatMoney(line.cost.totalAmount));
          break;
        case "url":
          if (child instanceof HTMLAnchorElement) {
            // Respecte le préfixe productsBase configuré (défaut: "product").
            child.href = `/${config.productsBase}/${line.merchandise.product.handle}`;
          }
          break;
        case "vendor":
          setText(child, line.merchandise.product.vendor);
          break;
        case "sku":
          setText(child, line.merchandise.sku ?? "");
          break;
        default:
          break;
      }
    });

  attachLineActions(clone, line);
}

// Keep one DOM node per Shopify line and template. Updating quantities must not
// detach the focused button (Webflow closes its dropdown on that focusout).
const renderedCartItems = new WeakMap<HTMLElement, Map<string, HTMLElement>>();

function hydrateCartItems(root: ParentNode, cart: Cart | null): void {
  const templates = Array.from(root.querySelectorAll<HTMLElement>("cart-item")).filter(
    (el) => !el.hasAttribute("data-lmdr-cart-item-rendered") && !el.parentElement?.closest("cart-item"),
  );
  for (const template of templates) {
    const parent = template.parentElement;
    if (!parent) continue;
    template.style.display = "none";
    template.setAttribute("data-lmdr-cart-item-template", "true");
    let rendered = renderedCartItems.get(template);
    if (!rendered) { rendered = new Map(); renderedCartItems.set(template, rendered); }
    const lines = cart?.lines ?? [];
    const ids = new Set(lines.map((line) => line.id));
    for (const [id, element] of rendered) {
      if (ids.has(id)) continue;
      if (element.contains(document.activeElement)) {
        const toggle = element.closest(".w-dropdown")?.querySelector<HTMLElement>(".w-dropdown-toggle");
        if (toggle) toggle.focus({ preventScroll: true });
        else { parent.tabIndex = -1; parent.focus({ preventScroll: true }); }
      }
      element.remove();
      rendered.delete(id);
    }
    for (const line of lines) {
      let element = rendered.get(line.id);
      if (!element) {
        element = template.cloneNode(true) as HTMLElement;
        element.removeAttribute("data-lmdr-cart-item-template");
        element.style.removeProperty("display");
        element.setAttribute("data-lmdr-cart-item-rendered", "true");
        // Webflow template IDs cannot be duplicated across line items.
        element.removeAttribute("id");
        element.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
        rendered.set(line.id, element);
      }
      fillCartItem(element, line);
    }
    let before: Node = template;
    for (const line of [...lines].reverse()) {
      const element = rendered.get(line.id)!;
      if (element.nextSibling !== before || element.parentElement !== parent) parent.insertBefore(element, before);
      before = element;
    }
  }
}

// ---------------------------------------------------------------------------
//  Public
// ---------------------------------------------------------------------------

export interface HydrateCartOptions {
  root: ParentNode;
  cart: Cart | null;
}

export function hydrateCart({ root, cart }: HydrateCartOptions): void {
  hydrateCartIndicators(root, cart);
  hydrateCartItems(root, cart);
  fixupCartForms(root, cart);
  hydrateDiscounts(root, cart);
  hydrateOrderNote(root, cart);
  bindCouponForm(root);
}

// ---------------------------------------------------------------------------
//  Discount title + amount hydration
// ---------------------------------------------------------------------------

/**
 * Récupère le premier discount (code ou auto) actif sur le panier.
 * Renvoie null si aucun discount appliqué.
 */
function firstActiveDiscount(cart: Cart | null): {
  title: string;
  amount: string;
} | null {
  if (!cart) return null;
  // Priorité 1 : discountAllocations (contient le montant effectivement
  // appliqué — peut différer du code demandé).
  const alloc = cart.discountAllocations?.[0];
  if (alloc) {
    return {
      title: alloc.title ?? alloc.code ?? "Réduction",
      amount: `−${formatMoney(alloc.discountedAmount)}`,
    };
  }
  // Priorité 2 : codes "applicable" sans allocation (rare).
  const applicable = (cart.discountCodes ?? []).filter((c) => c.applicable);
  if (applicable.length > 0) {
    return {
      title: applicable.map((c) => c.code).join(", "),
      amount: "",
    };
  }
  return null;
}

/**
 * Peuple les nodes `[discount="title"]` / `[discount="amount"]` et lève le
 * masquage FOUC sur leur wrapper (`.discount-block` / `[cart-item="discount"]`).
 *
 * Si aucun discount actif :
 *   - Wrapper + labels restent masqués par le critical CSS.
 *   - L'icône tag SVG dans le wrapper reste invisible aussi.
 */
function hydrateDiscounts(root: ParentNode, cart: Cart | null): void {
  const active = firstActiveDiscount(cart);

  // 1. Labels texte.
  const labels = root.querySelectorAll<HTMLElement>("[discount]");
  labels.forEach((el) => {
    const kind = el.getAttribute("discount") ?? "";
    if (!active) {
      el.removeAttribute("data-lmdr-discount");
      return;
    }
    switch (kind) {
      case "title":
        setText(el, active.title);
        break;
      case "amount":
        setText(el, active.amount);
        break;
      default:
        break;
    }
    el.setAttribute("data-lmdr-discount", "1");
  });

  // 2. Wrappers (.discount-block, [cart-item="discount"]) — inclure aussi
  //    leur icône SVG qu'on ne veut pas voir en l'absence de discount.
  const wrappers = root.querySelectorAll<HTMLElement>(
    '.discount-block, [cart-item="discount"]',
  );
  wrappers.forEach((el) => {
    if (active) {
      el.setAttribute("data-lmdr-discount", "1");
    } else {
      el.removeAttribute("data-lmdr-discount");
    }
  });
}

// ---------------------------------------------------------------------------
//  Order note (textarea name="Note")
// ---------------------------------------------------------------------------

const noteSelector = 'textarea[name="Note"], textarea[data-name="Note"], textarea.cart-notes';
interface NotePersistence {
  dirty: boolean;
  timer?: number;
  pending?: Promise<void>;
  pendingValue?: string;
}
const notePersistence = new WeakMap<HTMLTextAreaElement, NotePersistence>();

function persistOrderNote(ta: HTMLTextAreaElement): Promise<void> {
  const state = notePersistence.get(ta);
  if (!state) return Promise.resolve();
  if (state.timer !== undefined) { window.clearTimeout(state.timer); state.timer = undefined; }
  if (!state.dirty) return state.pending ?? Promise.resolve();
  const value = ta.value;
  if (state.pending && state.pendingValue === value) return state.pending;
  const pending = updateCartNote(value).then((cart) => {
    if (cart && ta.value === value) state.dirty = false;
  }).catch((error: unknown) => {
    showCommerceError(ta, "Impossible d’enregistrer votre note. Vérifiez votre connexion et réessayez.");
    throw error;
  }).finally(() => {
    if (state.pending === pending) {
      state.pending = undefined;
      state.pendingValue = undefined;
    }
  });
  state.pending = pending;
  state.pendingValue = value;
  return pending;
}

function hydrateOrderNote(root: ParentNode, cart: Cart | null): void {
  root.querySelectorAll<HTMLTextAreaElement>(noteSelector).forEach((ta) => {
    let state = notePersistence.get(ta);
    if (!state) {
      state = { dirty: !!ta.value && ta.value !== (cart?.note ?? "") };
      notePersistence.set(ta, state);
      ta.setAttribute("data-lmdr-note-bound", "true");
      if (!ta.value && cart?.note) ta.value = cart.note;
      const noteState = state;
      const save = (): void => { void persistOrderNote(ta).catch(() => undefined); };
      ta.addEventListener("input", () => {
        noteState.dirty = true;
        if (noteState.timer !== undefined) window.clearTimeout(noteState.timer);
        noteState.timer = window.setTimeout(save, 400);
      });
      ta.addEventListener("blur", save);
    } else if (!state.dirty && !state.pending && document.activeElement !== ta) {
      ta.value = cart?.note ?? "";
    }
  });
}

// ---------------------------------------------------------------------------
//  Coupon form (input name="Coupon" + button [data-action="coupon"])
// ---------------------------------------------------------------------------

function bindCouponForm(root: ParentNode): void {
  const buttons = root.querySelectorAll<HTMLElement>('[data-action="coupon"]');
  buttons.forEach((btn) => {
    if (btn.hasAttribute("data-lmdr-coupon-bound")) return;
    btn.setAttribute("data-lmdr-coupon-bound", "true");

    // CRITIQUE : force type="button" sur le bouton coupon pour qu'un click
    // NE déclenche PAS un submit implicite du <form> parent. Le form parent
    // contient aussi le bouton "Paiement" (checkout) et les cart-items ; un
    // submit de ce form tape sur `fixupCartForms` qui appelle goToCheckout
    // (ou autre), et on perdrait le contrôle du comportement coupon.
    if (btn instanceof HTMLButtonElement) {
      btn.type = "button";
    } else if (btn.tagName === "BUTTON") {
      btn.setAttribute("type", "button");
    }

    // Résolution de l'input : on ne remonte PAS au <form> (qui englobe tout
    // le panier) — on reste dans le container direct `.coupon-form` ou
    // `.coupon-wrap`, ou à défaut le parent immédiat du bouton.
    const scope =
      btn.closest<HTMLElement>(".coupon-form, .coupon-wrap, .coupon") ??
      btn.parentElement ??
      (root instanceof Element ? root : document.body);

    const onApply = async (ev: Event): Promise<void> => {
      ev.preventDefault();
      ev.stopPropagation();
      const input =
        scope.querySelector<HTMLInputElement>('input[name="Coupon"]') ??
        scope.querySelector<HTMLInputElement>('input[name*="oupon" i]') ??
        null;
      const code = input?.value?.trim() ?? "";
      if (!code) {
        setCouponFeedback(scope, input, {
          kind: "error",
          message: "Veuillez entrer un code de réduction.",
        });
        return;
      }
      btn.setAttribute("data-state", "loading");
      setCouponFeedback(scope, input, { kind: "loading" });
      try {
        await applyCouponCode(code);
        setCouponFeedback(scope, input, {
          kind: "success",
          message: "Code de réduction appliqué.",
        });
        // Efface l'input une fois le code accepté (comme Shopify le fait).
        if (input) input.value = "";
      } catch (err) {
        const isInvalid = err instanceof InvalidCouponError;
        setCouponFeedback(scope, input, {
          kind: "error",
          message: isInvalid
            ? `Le code « ${code} » n'est pas valide.`
            : "Impossible d'appliquer ce code. Réessayez.",
        });
        if (!isInvalid) {
          // eslint-disable-next-line no-console
          console.warn("[lmdr] applyCouponCode failed:", err);
        }
      } finally {
        btn.setAttribute("data-state", "default");
      }
    };

    btn.addEventListener("click", (ev) => {
      void onApply(ev);
    });

    // Enter dans l'input : on écoute keydown sur l'input lui-même (pas
    // sur le form parent, évite d'interférer avec le submit checkout).
    const input = scope.querySelector<HTMLInputElement>('input[name="Coupon"]');
    if (input && !input.hasAttribute("data-lmdr-coupon-input-bound")) {
      input.setAttribute("data-lmdr-coupon-input-bound", "true");
      input.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          ev.stopPropagation();
          void onApply(ev);
        }
      });
      // Efface l'erreur dès que l'utilisateur tape à nouveau.
      input.addEventListener("input", () => {
        setCouponFeedback(scope, input, { kind: "idle" });
      });
    }
  });
}

/**
 * Injecte un message de feedback sous le champ coupon. Le message est un
 * `<div class="lmdr-coupon-feedback">` posé dans le scope `.coupon-form`.
 * Idempotent : remplace le contenu existant.
 *
 * Le style est inliné minimalement pour marcher sans CSS custom, mais
 * l'utilisateur peut override via `.lmdr-coupon-feedback[data-kind="error"] {...}`
 * dans son CSS Webflow.
 */
interface CouponFeedback {
  kind: "idle" | "loading" | "success" | "error";
  message?: string;
}

function setCouponFeedback(
  scope: Element,
  input: HTMLInputElement | null,
  feedback: CouponFeedback,
): void {
  // On insère le feedback APRÈS le `.coupon-form` (sibling), pas dedans. Le
  // `.coupon-form` est un flex-row (input + bouton côte à côte) ; un enfant
  // supplémentaire dedans flotterait à droite du bouton. On veut le feedback
  // sur une nouvelle ligne, pleine largeur.
  const host = (scope.parentElement ?? scope) as HTMLElement;
  let el = host.querySelector<HTMLElement>(":scope > .lmdr-coupon-feedback");
  if (!el) {
    el = document.createElement("div");
    el.className = "lmdr-coupon-feedback";
    el.setAttribute("aria-live", "polite");
    el.style.fontSize = "0.875rem";
    el.style.marginTop = "0.4rem";
    el.style.lineHeight = "1.3";
    el.style.width = "100%";
    el.style.display = "block";
    // Insertion juste après le `.coupon-form` (scope) pour coller visuellement.
    if (scope.parentElement && scope.nextSibling) {
      scope.parentElement.insertBefore(el, scope.nextSibling);
    } else if (scope.parentElement) {
      scope.parentElement.appendChild(el);
    } else {
      host.appendChild(el);
    }
  }

  el.setAttribute("data-kind", feedback.kind);

  if (feedback.kind === "idle" || !feedback.message) {
    el.textContent = "";
    el.style.display = "none";
    input?.removeAttribute("aria-invalid");
    input?.style.removeProperty("border-color");
    return;
  }

  el.style.display = "";
  el.textContent = feedback.message;

  // Couleur par type (simple override via CSS possible).
  if (feedback.kind === "error") {
    el.style.color = "#b00020";
    input?.setAttribute("aria-invalid", "true");
    if (input) input.style.borderColor = "#b00020";
  } else if (feedback.kind === "success") {
    el.style.color = "#118a4e";
    input?.removeAttribute("aria-invalid");
    input?.style.removeProperty("border-color");
  } else {
    el.style.color = "";
    input?.removeAttribute("aria-invalid");
    input?.style.removeProperty("border-color");
  }
}

/**
 * Sur tous les forms du panier (mini-cart drawer + cart.html), on :
 *   1. Désactive la validation HTML5 (sinon le submit event ne fire pas avec
 *      les required="" vides — voir le bug résolu pour smootify-add-to-cart).
 *   2. Pré-remplit les inputs number vides avec "1" ou leur placeholder.
 *   3. Wire le bouton "Aller au paiement" (`.go-to-checkout`) vers
 *      `cart.checkoutUrl` au click — sinon Webflow envoie un submit générique
 *      qui affiche son "Thank you" et perd la redirection Shopify.
 */
function fixupCartForms(root: ParentNode, cart: Cart | null): void {
  // Cible : les forms contenant un [cart-item] ou un [cart="total"] (= cart forms).
  const cartForms = new Set<HTMLFormElement>();
  root.querySelectorAll<HTMLElement>("[cart-item], [cart=\"total\"]").forEach((el) => {
    const f = el.closest("form");
    if (f) cartForms.add(f);
  });

  cartForms.forEach((form) => {
    manageCommerceForm(form);
    const empty = !cart?.lines.length;
    const wrapper = form.parentElement;
    const emptyState = wrapper?.querySelector<HTMLElement>(':scope > [cart="is-empty"], :scope > .w-form-done');
    if (emptyState) {
      emptyState.setAttribute("cart", "is-empty");
      emptyState.classList.toggle("lmdr-hidden", !empty);
      emptyState.style.display = empty ? "block" : "none";
      form.classList.toggle("lmdr-hidden", empty);
    }
    form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(".go-to-checkout").forEach((button) => {
      button.disabled = empty || form.getAttribute("data-lmdr-checkout-pending") === "true";
      button.setAttribute("aria-disabled", String(button.disabled));
    });
    if (!form.hasAttribute("data-lmdr-cart-managed")) {
      form.setAttribute("data-lmdr-cart-managed", "true");
      form.setAttribute("action", "javascript:void(0)");
      form.noValidate = true;
      // Intercepte tout submit qui serait forgé par un button type="submit"
      // dans le form (ex: .go-to-checkout).
      form.addEventListener(
        "submit",
        (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          ev.stopImmediatePropagation();
          void goToCheckout(form);
        },
        true,
      );
      // Click-level fallback pour les boutons .go-to-checkout (inputs submit).
      form.querySelectorAll<HTMLElement>(".go-to-checkout").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          void goToCheckout(form);
        });
      });
      // Hide le w-form-done / w-form-fail de ce form panier aussi.
      const wrap = form.parentElement;
      wrap?.querySelectorAll<HTMLElement>(".w-form-fail").forEach((n) => {
        n.style.display = "none";
      });
    }

    // Pré-remplit l'input quantity si vide (le template Webflow a juste un
    // placeholder="1" mais value="").
    form
      .querySelectorAll<HTMLInputElement>('input[type="number"], input[name="quantity"]')
      .forEach((inp) => {
        if (inp.value || document.activeElement === inp || inp.hasAttribute("data-lmdr-committing")) return;
        const ph = inp.getAttribute("placeholder");
        inp.value = ph && /^\d+$/.test(ph) ? ph : "1";
      });
  });
}

/**
 * Redirige vers le checkout Shopify si le cart courant a une URL, sinon
 * warn console (évite de planter l'UX).
 */
async function goToCheckout(form: HTMLFormElement): Promise<void> {
  if (form.getAttribute("data-lmdr-checkout-pending") === "true") return;
  form.setAttribute("data-lmdr-checkout-pending", "true");
  showCommerceError(form, null);
  form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(".go-to-checkout").forEach((b) => { b.disabled = true; });
  try {
    // Blur is not guaranteed for keyboard or touch checkout. Flush drafts and
    // debounced notes explicitly before waiting for the mutation queue.
    const quantities = Array.from(form.querySelectorAll<HTMLInputElement>(
      '[data-lmdr-cart-item-rendered] input[data-lmdr-qty-bound]',
    ));
    const quantityResults = await Promise.all(quantities.map((input) => {
      const lineId = input.closest<HTMLElement>("[data-line-id]")?.dataset["lineId"];
      return lineId ? commitQuantity(input, lineId) : Promise.resolve(true);
    }));
    if (quantityResults.some((success) => !success)) throw new Error("Quantity update failed");
    await Promise.all(Array.from(document.querySelectorAll<HTMLTextAreaElement>(noteSelector)).map(persistOrderNote));
    const cart = await whenCartIdle();
    if (!cart?.lines.length || !cart.checkoutUrl) return;
    window.location.assign(cart.checkoutUrl);
  } catch {
    showCommerceError(form, "Le panier n’a pas pu être vérifié. Vérifiez votre connexion et réessayez.");
  } finally {
    form.removeAttribute("data-lmdr-checkout-pending");
    form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(".go-to-checkout").forEach((b) => { b.disabled = !getCurrentCart()?.lines.length; });
  }
}

interface WebflowDropdownState { open: boolean; }
interface DropdownJQuery {
  (element: Element): { triggerHandler(event: string): void };
  data(element: Element, key: string): WebflowDropdownState | undefined;
}

function cartDropdowns(doc: Document): HTMLElement[] {
  return Array.from(doc.querySelectorAll<HTMLElement>(".w-dropdown"))
    .filter((dd) => !!dd.querySelector(".cart-dropdown"));
}

function setDrawerOpen(dd: HTMLElement, open: boolean): boolean {
  const list = dd.querySelector<HTMLElement>(".w-dropdown-list");
  const toggle = dd.querySelector<HTMLElement>(".w-dropdown-toggle");
  if (!list || !toggle) return false;
  const browser = window as unknown as { jQuery?: DropdownJQuery; Webflow?: { env?: { touch?: boolean } } };
  const jq = browser.jQuery;
  const state = jq?.data(dd, ".w-dropdown");
  // The native controller owns its state, classes, outside clicks and Escape.
  // Trigger its handler rather than changing classes behind its back.
  dd.classList.remove("w--open");
  if (state && jq) {
    if (state.open === open) return false;
    if (open) jq(toggle).triggerHandler(browser.Webflow?.env?.touch ? "click.w-dropdown" : "mouseup.w-dropdown");
    else jq(dd).triggerHandler("w-close.w-dropdown");
    return true;
  }
  const changed = list.classList.contains("w--open") !== open;
  list.classList.toggle("w--open", open);
  toggle.classList.toggle("w--open", open);
  toggle.setAttribute("aria-expanded", String(open));
  return changed;
}

export function openCartDrawer(doc: Document = document, opts: { auto?: boolean } = {}): boolean {
  let opened = false;
  for (const dd of cartDropdowns(doc)) {
    if (dd.getClientRects().length === 0) continue;
    const policy = dd.getAttribute("data-open") ?? dd.closest("[data-open]")?.getAttribute("data-open") ??
      dd.querySelector("[data-open]")?.getAttribute("data-open") ?? "on-add";
    if (opts.auto && policy !== "on-add") continue;
    opened = setDrawerOpen(dd, true) || opened;
  }
  return opened;
}

export function closeCartDrawer(doc: Document = document): boolean {
  let closed = false;
  for (const dd of cartDropdowns(doc)) closed = setDrawerOpen(dd, false) || closed;
  return closed;
}

/**
 * Bind les boutons [data-trigger="open-cart"] / [data-trigger="close-cart"]
 * pour permettre le contrôle manuel du mini-cart (utile quand
 * `data-open="never"` ou "manual"). Idempotent.
 */
export function attachCartTriggers(doc: Document = document): void {
  const marker = doc as Document & { __lmdrDrawerEvents?: boolean };
  if (!marker.__lmdrDrawerEvents) {
    marker.__lmdrDrawerEvents = true;
    doc.addEventListener("smootify:added_to_cart", (event) => {
      const detail = (event as CustomEvent<{ goToCheckout?: boolean }>).detail;
      if (!detail?.goToCheckout) openCartDrawer(doc, { auto: true });
    });
    // Used only if the native Webflow dropdown module is unavailable.
    doc.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const toggle = target?.closest<HTMLElement>(".w-dropdown-toggle");
      const dd = toggle?.parentElement;
      const jq = (window as unknown as { jQuery?: DropdownJQuery }).jQuery;
      if (dd?.querySelector(".cart-dropdown") && !jq?.data(dd, ".w-dropdown")) {
        event.preventDefault();
        setDrawerOpen(dd, !dd.querySelector(".w-dropdown-list")?.classList.contains("w--open"));
      }
    });
  }
  const openBtns = doc.querySelectorAll<HTMLElement>(
    '[data-trigger="open-cart"]',
  );
  openBtns.forEach((btn) => {
    if (btn.hasAttribute("data-lmdr-cart-trigger-bound")) return;
    btn.setAttribute("data-lmdr-cart-trigger-bound", "true");
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      openCartDrawer(doc);
    });
  });
  const closeBtns = doc.querySelectorAll<HTMLElement>(
    '[data-trigger="close-cart"]',
  );
  closeBtns.forEach((btn) => {
    if (btn.hasAttribute("data-lmdr-cart-trigger-bound")) return;
    btn.setAttribute("data-lmdr-cart-trigger-bound", "true");
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      closeCartDrawer(doc);
    });
  });
}

/**
 * Attach a global listener that re-hydrates the whole document on
 * `smootify:cart_updated`. Safe to call multiple times — we deduplicate via
 * a flag on the document.
 */
interface DocumentWithFlag extends Document {
  __lmdrCartListenerAttached?: boolean;
}

export function attachCartAutoRefresh(root: ParentNode = document): void {
  const doc = (typeof document !== "undefined" ? document : null) as DocumentWithFlag | null;
  if (!doc) return;
  if (doc.__lmdrCartListenerAttached) return;
  doc.__lmdrCartListenerAttached = true;

  doc.addEventListener("smootify:error", (ev) => {
    if ((ev as CustomEvent<{ type?: string }>).detail?.type !== "cart_load") return;
    hydrateCart({ root, cart: getCurrentCart() });
    const forms = new Set<HTMLFormElement>();
    root.querySelectorAll('[cart-item], [cart="total"]').forEach((node) => {
      const form = node.closest("form");
      if (form) forms.add(form);
    });
    forms.forEach((form) => {
      const wrapper = form.parentElement ?? form;
      wrapper.querySelectorAll<HTMLElement>('[cart="is-empty"]').forEach((node) => {
        node.classList.add("lmdr-hidden");
        node.style.display = "none";
      });
      let error = wrapper.querySelector<HTMLElement>(":scope > [data-lmdr-cart-load-error]");
      if (!error) {
        error = document.createElement("div");
        error.setAttribute("data-lmdr-cart-load-error", "true");
        error.setAttribute("role", "alert");
        error.style.cssText = "padding:16px;line-height:1.5;color:#a32622";
        const message = document.createElement("p");
        message.textContent = "Impossible de charger le panier. Vérifiez votre connexion puis réessayez.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.textContent = "Réessayer";
        retry.addEventListener("click", () => {
          retry.disabled = true;
          void loadCart().catch(() => { retry.disabled = false; });
        });
        error.append(message, retry);
        wrapper.appendChild(error);
      }
      form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(".go-to-checkout").forEach((button) => {
        button.disabled = true;
      });
    });
    if (!getCurrentCart()) root.querySelectorAll<HTMLElement>('[cart="count"], [cart="total"], [cart="subtotal"]').forEach((node) => {
      node.textContent = "—";
    });
  });

  doc.addEventListener("smootify:cart_updated", (ev) => {
    root.querySelectorAll("[data-lmdr-cart-load-error]").forEach((error) => error.remove());
    // Per Smootify doc: detail = cart (directly). On accepte aussi la forme
    // {cart} au cas où du code custom dispatch l'event avec l'ancien format.
    const detail = (ev as CustomEvent<Cart | { cart?: Cart | null } | null>).detail;
    let cart: Cart | null;
    if (!detail) cart = getCurrentCart();
    else if (typeof detail === "object" && "id" in detail && "lines" in detail) {
      cart = detail as Cart;
    } else if (typeof detail === "object" && "cart" in detail) {
      cart = (detail.cart as Cart | null) ?? getCurrentCart();
    } else {
      cart = getCurrentCart();
    }
    hydrateCart({ root, cart });
  });
}
